import { Component, Input } from '@angular/core';
import { Edition } from '@core/models/edition.model';
import { Inscription } from '@core/models/inscription.model';
import { Payment } from '@core/models/payment.model';

@Component({
  selector: 'gt-inscriptions-payments',
  templateUrl: './inscriptions-payments.component.html',
  styles: ``,
})
export class InscriptionsPaymentsComponent {
  @Input() inscription!: Inscription;
  @Input() edition!: Edition;
  @Input() showAddService = false;
  @Input() payments: Payment[] = [];

  get allServicesAreRequested(): boolean {
    if (!this.inscription || !this.edition) return false;

    // Filter out payOffline services - they don't count
    const onlineServices = this.edition.extraServices?.filter((service) => !service.payOffline) ?? [];

    // If there are no online services, nothing to request
    if (onlineServices.length === 0) return true;

    return onlineServices.every((service) => {
      const addedService = this.inscription.extraServices?.find((ie) => ie.id === service.id);

      // Service must be added to be considered "all requested"
      if (!addedService) {
        return false;
      }

      // Check if service has been requested at maximum capacity
      if (service.byPerson) {
        // For byPerson services, must match the number of participants
        return addedService.quantity >= (this.inscription.summonedParticipants?.length ?? 0);
      } else {
        // For non-byPerson services, must match maxQuantity
        return addedService.quantity >= (service.maxQuantity ?? 1);
      }
    });
  }

  get totalAmount(): number {
    if (!this.inscription || !this.edition) return 0;

    // Precio de la tarifa
    const ratePrice = this.inscription.rate?.price || 0;

    // Precio de todos los servicios extra
    const extraServicesTotal = (this.edition.extraServices || []).reduce((acc, editionService) => {
      const inscriptionService = this.inscription.extraServices?.find((es) => es.id === editionService.id);
      const servicePrice = inscriptionService?.price ?? editionService.price;

      if (editionService.mandatory) {
        // Servicios obligatorios: usar cantidad real de la inscripción
        const quantity =
          inscriptionService?.quantity ??
          (editionService.byPerson ? this.inscription.summonedParticipants?.length || 0 : 1);
        return acc + servicePrice * quantity;
      } else if (inscriptionService && inscriptionService.quantity > 0) {
        // Servicios opcionales: usar cantidad inscrita
        return acc + servicePrice * inscriptionService.quantity;
      }

      return acc;
    }, 0);

    // Precio del pack premium si aplica
    const premiumPackPrice = this.inscription.isPremium ? this.inscription.premiumPack?.price || 0 : 0;

    return ratePrice + extraServicesTotal + premiumPackPrice;
  }

  get totalOnlineAmount(): number {
    if (!this.inscription || !this.edition) return 0;

    // Precio de la tarifa
    const ratePrice = this.inscription.rate?.price || 0;

    // Precio de los servicios extra que NO son offline
    const extraServicesTotal = (this.edition.extraServices || []).reduce((acc, editionService) => {
      if (editionService.payOffline) return acc;

      const inscriptionService = this.inscription.extraServices?.find((es) => es.id === editionService.id);
      const servicePrice = inscriptionService?.price ?? editionService.price;

      if (editionService.mandatory) {
        const quantity =
          inscriptionService?.quantity ??
          (editionService.byPerson ? this.inscription.summonedParticipants?.length || 0 : 1);
        return acc + servicePrice * quantity;
      } else if (inscriptionService && inscriptionService.quantity > 0) {
        return acc + servicePrice * inscriptionService.quantity;
      }

      return acc;
    }, 0);

    const premiumPackPrice = this.inscription.isPremium ? this.inscription.premiumPack?.price || 0 : 0;

    return ratePrice + extraServicesTotal + premiumPackPrice;
  }

  get totalOfflineAmount(): number {
    if (!this.inscription || !this.edition) return 0;

    // Precio de los servicios extra que son offline
    return (this.edition.extraServices || []).reduce((acc, editionService) => {
      if (!editionService.payOffline) return acc;

      const inscriptionService = this.inscription.extraServices?.find((es) => es.id === editionService.id);
      const servicePrice = inscriptionService?.price ?? editionService.price;

      if (editionService.mandatory) {
        const quantity =
          inscriptionService?.quantity ??
          (editionService.byPerson ? this.inscription.summonedParticipants?.length || 0 : 1);
        return acc + servicePrice * quantity;
      } else if (inscriptionService && inscriptionService.quantity > 0) {
        return acc + servicePrice * inscriptionService.quantity;
      }

      return acc;
    }, 0);
  }

  get paidAmount(): number {
    return (this.payments || [])
      .filter((payment) => payment.status === 'SUCCESS')
      .reduce((acc, payment) => acc + payment.amount, 0);
  }

  get pendingOnlineAmount(): number {
    return this.totalOnlineAmount - this.paidAmount;
  }

  get pendingOfflineAmount(): number {
    return this.totalOfflineAmount;
  }

  get selectedRate() {
    if (!this.inscription || !this.edition) return null;
    const editionRate = this.edition.rates?.find((rate) => rate.id === this.inscription.rate.id);
    if (!editionRate) return null;
    return { ...editionRate, price: this.inscription.rate.price };
  }

  get selectedExtraServices() {
    if (!this.inscription || !this.edition) return [];
    return (this.inscription.extraServices || [])
      .filter((extraService) => extraService.quantity > 0)
      .map((extraService) => {
        const editionService = this.edition.extraServices?.find((es) => es.id === extraService.id);
        return editionService
          ? { ...editionService, quantity: extraService.quantity, price: extraService.price }
          : null;
      })
      .filter(Boolean);
  }

  get selectedExtraServicesOffline() {
    if (!this.inscription || !this.edition) return [];
    return (this.edition.extraServices || [])
      .filter((editionService) => editionService.payOffline && editionService.mandatory)
      .map((editionService) => {
        const inscriptionService = this.inscription.extraServices?.find((es) => es.id === editionService.id);
        return {
          ...editionService,
          quantity: inscriptionService?.quantity ?? 1,
          price: inscriptionService?.price ?? editionService.price,
        };
      });
  }
}
